import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { format } from 'date-fns';
import keyBy from 'lodash/keyBy';
import { combineLatest, EMPTY, of, Subscription } from 'rxjs';
import { filter, finalize, map, switchMap } from 'rxjs/operators';

import { labelMinLength, labelMaxLength } from '../../app.constants';
import { BatchJobDialog } from '../batch-job-dialog/batch-job-dialog.component';
import { ConfigService } from '../../config.service';
import { FwLegacyDialogService as DialogService } from '../../dialogs/dialog.service';
import { FileBrowserDialog, SuggestedFileOption } from '../file-browser/file-browser-dialog.component';
import { CoreApiService } from '../../core-api.service';
import { GearConfigComponent } from '../gear-config/gear-config.component';
import { ContextWithOption, SelectGearContext } from '../gear-version-selector/gear-version-selector.component';
import { GearService } from '../gear.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerType, isLabEdition } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { JobPriority } from '../../shared/enums/job-priority.enum';
import { AddAnalysis } from '../../shared/models/analysis.model';
import { ClassicBatchProposalInput } from '../../shared/models/batch.model';
import { Container, ContainerReference } from '../../shared/models/container.model';
import { RelatedContainer } from '../../shared/models/gear-suggestions.model';
import { Gear, GearInput } from '../../shared/models/gear.model';
import { AddJob, JobContextInputs, JobInput } from '../../shared/models/job.model';
import { firstValueFrom, replayValue } from '../../shared/observable.utils';

export interface RunGearOptions {
  batchMode?: boolean
  collectionId?: string
  containers: ContainerReference[]
  context?: SelectGearContext
  contextOptions?: Record<SelectGearContext, boolean>
  gears?: Gear[]
  gearFilter?: (gear: Gear) => boolean
}

type KeyedGearInput = GearInput & { key: string };

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'run-gear-dialog',
  },
  selector: 'run-gear-dialog',
  styleUrls: ['./run-gear-dialog.component.scss'],
  templateUrl: './run-gear-dialog.component.html',
})
export class RunGearDialog implements OnInit, OnDestroy {
  @ViewChild(GearConfigComponent, { static: false }) gearConfig?: GearConfigComponent

  billingLabels: Record<string, string> = {} // gear.gear.name -> label
  collectionId = this.data.collectionId
  containers = this.data.containers
  context: ContextWithOption = this.data.context ?? 'option'
  contextInputs: JobContextInputs = null
  contextOptions = this.data.contextOptions ?? { analysis: true, data: true }
  forms = {
    gearSelection: new FormControl({ name: null, id: null, label: null }),
    inputs: new FormGroup({}),
    optionalInputPolicy: new FormControl(null, [Validators.required]),
    outputContainer: new FormGroup({
      label: new FormControl(null, [Validators.required]),
      container: new FormControl(null),
    }),
    analysisLabel: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(labelMaxLength)]),
    tags: new FormControl([]),
    config: new FormControl({}),
    priority: new FormControl(JobPriority.Medium),
  }
  gears: Record<string, Gear> = {}
  isLoading = true
  isSaving = false
  isValid$ = of(false)
  priorities = Object.values(JobPriority)
  jobPrioritiesEnabled = this.configService.isFeatureEnabled('job_prioritization')

  private subscriptions = {
    bootstrap: Subscription.EMPTY,
    container: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) private data: RunGearOptions,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private gearService: GearService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
    private ref: MatDialogRef<RunGearDialog, void>,
  ) {
    this.subscriptions.container = this.coreApi.containers.get<Container>(this.containers[0]._id).subscribe((container) => {
      const projectId = container.parents.project || container._id;
      this.subscriptions.bootstrap = combineLatest([
        this.gearService.getLatestVersions({ projectId }), // for performance, remaining versions loaded in gear-version-selector
        this.projectsService.getProjects(),
        this.projectsService.getGroups(),
        this.configService.getSiteSettings(),
      ]).subscribe(([gears, projects, groups, siteSettings]) => {
        this.isLoading = false;
        // use gears if present, apply gear filter if present
        this.gears = keyBy((this.data.gears ?? gears).filter(this.data.gearFilter ?? (() => true)), '_id');
        if (configService.isFeatureEnabled('multiproject')) {
          const project = projects.find(({ _id }) => _id === projectId);
          const centerGears = new Set(siteSettings.center_gears || []);
          if (project && isLabEdition(project)) {
            // display which lab/group will be billed
            const group = groups.find(({ _id }) => _id === container.parents.group);
            const billingLabel = group?.label || project.group;
            for (const gear of Object.values(this.gears)) {
              if (!centerGears.has(gear.gear.name)) {
                this.billingLabels[gear.gear.name] = billingLabel;
              }
            }
          } else {
            // filter down to gears that are center-billed
            this.gears = keyBy(Object.values(this.gears).filter(gear => centerGears.has(gear.gear.name)), '_id');
          }
        }
        if (this.context === 'option' && !this.hasContextOptions) {
          this.context = Object.keys(this.contextOptions)
            .find(context => this.contextOptions[context]) as SelectGearContext;
        }
      });
    });

    this.forms.gearSelection.valueChanges.subscribe(() => {
      const gear = this.gears[this.selectedGearId];

      if (this.isBatch) {
        // batch inputs are determined when jobs run
        this.forms.inputs = new FormGroup({});
      } else {
        this.forms.inputs = new FormGroup(Object.assign({},
          ...Object.entries(gear?.gear.inputs ?? {})
            .filter(([, input]) => input.base === 'file')
            .map(([key, input]) => {
              const group = new FormGroup({
                label: new FormControl(null, input.optional ? [] : [Validators.required]),
                file: new FormControl(null),
              });
              return { [key]: group };
            })
        ));
        this.forms.inputs.markAllAsTouched(); // light up required
      }

      // redefine because we restructured inputs
      this.isValid$ = combineLatest(
        Object.values(this.forms).map(control => replayValue(control))
      ).pipe(map(() => Boolean(
        this.selectedGearId
          // !invalid to catch VALID and DISABLED status
          && Object.values(this.forms).every(form => !form.invalid)
          && this.gearConfig && !this.gearConfig.hasErrors
      )));

      if (this.context === 'analysis') {
        this.forms.optionalInputPolicy.disable();
        this.forms.analysisLabel.enable();
        this.forms.analysisLabel.setValue(gear ? `${gear.gear.name} ${format(new Date(), 'MM/dd/yyyy HH:mm:ss')}` : '');
        this.forms.outputContainer.disable();
      } else {
        // utility gear
        this.forms.outputContainer.reset();
        /**
         * If  > 1 required file inputs, allow the output container to be changed, and default to the first input's parent
         * If == 1 required file input , do not allow output container to be changed, and set output based on parent of required file
         * If == 0 required file inputs, do not allow output container to be changed, and set output to initial target container
         */
        if (this.requiredFileInputs.length > 1 && !this.isBatch) {
          this.forms.outputContainer.enable();
          this.forms.outputContainer.markAllAsTouched(); // light up required
        } else {
          this.forms.outputContainer.disable();
        }
        this.forms.analysisLabel.disable();
      }
      if (this.isBatch && this.optionalFileInputs.length > 0) {
        this.forms.optionalInputPolicy.enable();
        this.forms.optionalInputPolicy.markAsTouched(); // light up required
      } else {
        this.forms.optionalInputPolicy.disable();
      }
    });

    this.forms.outputContainer.valueChanges.pipe(
      switchMap(() => {
        const container: RelatedContainer = this.forms.outputContainer.get('container').value;
        if (container) {
          return this.coreApi.gears.context(this.selectedGearId, container.cont_type as ContainerType, container._id);
        }
        return of(null as JobContextInputs);
      })
    ).subscribe(contextInputs => {
      this.contextInputs = contextInputs;
    });
  }

  ngOnInit(): void {
    this.ref.addPanelClass('padding-0');
    this.ref.updatePosition({ top: '30px' });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isBatch(): boolean {
    return Boolean(this.data.batchMode);
  }

  get hasContextOptions(): boolean {
    return Object.values(this.contextOptions).filter(Boolean).length > 1;
  }

  get selectedGearId(): string {
    return this.forms.gearSelection.value?.id;
  }

  get hasInputErrors(): boolean {
    return Object.keys(this.forms)
      .filter(key => ['inputs', 'optionalInputPolicy', 'outputContainer', 'analysisLabel', 'tags'].includes(key))
      .some(key => this.forms[key].invalid);
  }

  get fileInputs(): KeyedGearInput[] {
    return Object.entries(this.gears[this.selectedGearId]?.gear?.inputs ?? {})
      .filter(([, input]) => input.base === 'file')
      .map(([key, input]) => ({ ...input, key }));
  }

  get requiredFileInputs(): KeyedGearInput[] {
    return this.fileInputs.filter(input => !input.optional);
  }

  get optionalFileInputs(): KeyedGearInput[] {
    return this.fileInputs.filter(input => input.optional);
  }

  get apiKeyInput(): GearInput {
    return Object.values(this.gears[this.selectedGearId]?.gear?.inputs ?? {})
      .find(input => input.base === 'api-key');
  }

  // trackBy
  getInputKey(index: number, input: KeyedGearInput): string {
    return input.key;
  }

  selectGearInput(key: string): void {
    const container = this.containers[0];
    this.dialog.open(FileBrowserDialog, {
      data: {
        collectionId: this.collectionId,
        containerId: container._id,
        containerType: container.container_type ?? getContainerType(container as Container),
        gearContext: this.context,
        inputType: key,
        isOutput: false,
        gearId: this.selectedGearId,
      },
    }).afterClosed().pipe(
      switchMap((suggestedFile: SuggestedFileOption) => {
        if (suggestedFile) {
          this.forms.inputs.get(key).setValue({
            label: suggestedFile.file.name,
            file: suggestedFile,
          });
          /**
           * If  > 1 required file inputs, seed the output container value but allow users to change it
           * If == 1 required file input , set the output container to the file's parent as a hidden value
           */
          if (!this.forms.outputContainer.get('container').value && this.requiredFileInputs.length >= 1) {
            return this.coreApi.containers.get<Container>(suggestedFile.container_id);
          }
        }
        return EMPTY;
      }),
    ).subscribe(container => {
      this.forms.outputContainer.setValue({
        label: container.label,
        container: {
          _id: container._id,
          cont_type: getContainerType(container),
          label: container.label,
        } as RelatedContainer,
      });
    });
  }

  clearGearInput(key: string): void {
    this.forms.inputs.get(key).reset();
  }

  selectOutputLocation(): void {
    const container = this.containers[0];
    this.dialog.open(FileBrowserDialog, {
      data: {
        collectionId: this.collectionId,
        containerId: container._id,
        containerType: container.container_type ?? getContainerType(container as Container),
        gearContext: this.context,
        // inputType: 'container',
        isOutput: true,
        gearId: this.selectedGearId,
      },
    }).afterClosed().subscribe((relatedContainer: RelatedContainer) => {
      if (relatedContainer) {
        this.forms.outputContainer.setValue({
          label: relatedContainer.label,
          container: relatedContainer,
        });
      }
    });
  }

  async runGear(): Promise<void> {
    if (!(await firstValueFrom(this.isValid$))) {
      return;
    }
    this.isSaving = true;
    const job: AddJob = {
      gear_id: this.selectedGearId,
      tags: this.forms.tags.value,
      inputs: getInputValues(this.forms.inputs),
      config: this.forms.config.value,
    };
    if (this.jobPrioritiesEnabled) {
      job.priority = this.forms.priority.value;
    }
    const containers = this.containers.map(container => ({ type: getContainerType(container as Container), id: container._id }));
    if (this.context !== 'analysis') {
      const outputLocation: RelatedContainer = this.forms.outputContainer.get('container').value;
      if (outputLocation) {
        job.destination = { type: outputLocation.cont_type as ContainerType, id: outputLocation._id };
      } else {
        job.destination = containers[0];
      }
    }
    if (this.isBatch) {
      const batchJob: ClassicBatchProposalInput = {
        config: job.config,
        gear_id: job.gear_id,
        tags: job.tags,
        optional_input_policy: this.forms.optionalInputPolicy.value,
        targets: containers,
      };
      if (job.compute_provider_id) {
        batchJob.compute_provider_id = job.compute_provider_id;
      }
      if (job.priority) {
        batchJob.priority = job.priority;
      }
      if (this.collectionId) {
        batchJob.target_context = { type: ContainerType.Collection, id: this.collectionId };
      }
      if (this.context === 'analysis') {
        batchJob.analysis = { label: this.forms.analysisLabel.value };
      }
      this.coreApi.jobs.batch.create(batchJob).pipe(
        switchMap(batchJob => this.dialog.open(BatchJobDialog, {
          data: { batchJob },
        }).afterClosed().pipe(
          filter(proceed => batchJob.matched.length > 0 && Boolean(proceed)),
          switchMap(() => this.coreApi.jobs.batch.run(batchJob._id)),
        )),
        finalize(() => {
          this.isSaving = false;
        }),
      ).subscribe({
        next: () => {
          this.notifications.success('jobs.add.success');
          this.ref.close();
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('jobs.add.error');
        },
      });
      return;
    }

    if (this.context === 'analysis') {
      const analysis: AddAnalysis = {
        job: job,
        label: this.forms.analysisLabel.value,
      };
      this.coreApi.analyses.post(containers[0].type, containers[0].id, analysis).pipe(
        finalize(() => {
          this.isSaving = false;
        }),
      ).subscribe({
        next: () => {
          this.notifications.success('jobs.add.success');
          this.ref.close();
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('jobs.add.error');
        },
      });
      return;
    }

    this.coreApi.jobs.add(job).pipe(
      finalize(() => {
        this.isSaving = false;
      }),
    ).subscribe({
      next: () => {
        this.notifications.success('jobs.add.success');
        this.ref.close();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('jobs.add.error');
      },
    });
  }
}

function getInputValues(inputs: FormGroup): Record<string, JobInput> {
  const result = {};
  for (const [key, control] of Object.entries(inputs.controls)) {
    const option: SuggestedFileOption = control.get('file').value;
    if (option) {
      result[key] = { type: option.container_type, id: option.container_id, name: option.file.name };
    }
  }
  return result;
}
